
import { ReactNode, Suspense } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { ErrorBoundary } from "@/components/ErrorBoundary";

interface ContentLoaderProps {
  children: ReactNode;
  isLoading?: boolean;
  error?: Error | null;
  loadingMessage?: string;
}

export const ContentLoader = ({
  children,
  isLoading = false,
  error = null,
  loadingMessage = "Loading content..."
}: ContentLoaderProps) => {

  if (error) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="w-full"
      >
        <div className="flex flex-col items-center justify-center min-h-[200px] p-6 bg-slate-50 border border-slate-200 rounded-md">
          <div className="text-amber-500 mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-8 h-8"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </div>
          <h3 className="text-lg font-medium text-slate-900 mb-2">Error loading content</h3>
          <p className="text-sm text-slate-600 text-center">
            {error.message || "There was a problem loading this content."}
          </p>
        </div>
      </motion.div>
    );
  }

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="w-full flex flex-col items-center justify-center min-h-[200px] p-6"
      >
        <Loader2 className="h-8 w-8 text-primary animate-spin mb-4" />
        <p className="text-sm text-slate-600">{loadingMessage}</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full"
    >
      <ErrorBoundary>
        <Suspense
          fallback={
            <div className="flex flex-col items-center justify-center min-h-[200px] p-6">
              <Loader2 className="h-8 w-8 text-primary animate-spin mb-4" />
              <p className="text-sm text-slate-600">{loadingMessage}</p>
            </div>
          }
        >
          {children}
        </Suspense>
      </ErrorBoundary>
    </motion.div>
  );
};
